/*
 * Approved copy constants for the Governance Tile · E1 column.
 *
 * Every string is word-for-word from mockup v8.8.12 lines 681-943 and
 * carries [COPY-APPROVED: governance-tile-v8] (2026-07-24).
 *
 * Static mockup denominators (`of 48`) and mockup placeholder integers
 * ('25 of 48', '39 of 48', '4 of 48', '2 of 12', '01:24', '17', '432',
 * '13', etc.) are deliberately stripped from this manifest — every row
 * that carried them is now resolved live from the endpoint in
 * E1Column.jsx via WORK_LANDED_MAP / CONFIDENCE_MAP / WORDS_MAP /
 * LOOSE_ENDS_MAP, or renders an em dash with a `-pending` testid
 * marker when the endpoint field does not exist yet (attestation-
 * honesty rule · anchor GB-013 · § V.6).  Row objects expose only
 * `label` + optional `tooltip` + optional `bold` — no fabricated
 * values can leak through.
 */

export const E1_SCORECARD_TITLE = 'How E1 is going';
export const E1_SCORECARD_ICON = '\u2699';
export const E1_SUBTITLE =
  'Author of the code · target: task passes only when all 11 § V.6 checks are green';

export const E1_RITUALS_PARENT_LABEL = 'Ritual and best-practice compliance';
export const E1_RITUALS_PARENT_TOOLTIP =
  'Ritual and best-practice compliance. Three groups of blocking discipline checks that fire every session. If any sub-task fails, this section paints red and blocks ship until resolved.';

// Ritual sub-blocks.  Each group exposes `key`, `parent`, `tooltip`,
// `borderColour`, and `subChecks[].label` only.  All glyphs and chips
// are supplied live by the renderer (◯ for sub-check state and the
// literal 'Not measured yet' for the group chip) pending IJ-079.
export const E1_RITUALS = [
  {
    key: 'tss',
    parent: 'T-SS · Session Start Ritual',
    tooltip: 'T-SS Session Start. Auto-created as the FIRST ledger entry of every session. Seven sub-tasks.',
    borderColour: 'border-l-rose-500',
    subChecks: [
      { label: 'SS.1 · Read SPD_GOVERNANCE.md in detail (recall-tested)' },
      { label: 'SS.2 · Read last three PRD.md SPD entries in detail' },
      { label: 'SS.3 · Read STANDING_ORDERS.md · CHANGELOG · INTENT_LEDGER · GOVERNANCE_LEDGER · JPD_Intent_Judge · E1_Lessons' },
      { label: 'SS.4 · Read test_credentials.md and verify REACT_APP_BACKEND_URL' },
      { label: 'SS.5 · Verify OS clock via date -u' },
      { label: 'SS.6 · Write CHANGELOG session-start proof line as FIRST tool call (not retroactively)' },
      { label: 'SS.7 · If fork session, quote retiring-E1\u2019s "Pending operator directives · verbatim" block back to operator BEFORE any code · § U.b' },
    ],
  },
  {
    key: 'tse',
    parent: 'T-SE · Session End Ritual',
    tooltip: 'T-SE Session End. Auto-created as the LAST ledger entry of every session. Six sub-tasks.',
    borderColour: 'border-l-amber-500',
    subChecks: [
      { label: 'SE.1 · Update PRD.md + CHANGELOG.md with today\u2019s SPD entry (incl. § T docs_updated list)' },
      { label: 'SE.2 · Codify new class-of-failures as LESSON: lines in JPD_Intent_Judge.md AND E1_Lessons.md' },
      { label: 'SE.3 · Verify every user-approval token in the ledger has a matching operator [APPROVED:] reply' },
      { label: 'SE.4 · Sweep tmp files, resolve open loose ends, or backlog with a Sev tag' },
      { label: 'SE.5 · Write CHANGELOG session-end proof line as LAST tool call before finish' },
      { label: 'SE.6 · Emit "Pending operator directives · verbatim" block for next fork' },
    ],
  },
  {
    key: 'cbp',
    parent: 'Coding Best Practice (C1–C6)',
    tooltip: 'Six Coding Best Practice checks (C1 files ≤ 500 lines · C2 Domain-Driven · C3 Every Agent as a Product · C4 QA in Every Layer · C5 Full Observability · C6 Version Everything). Every code turn must clear all six.',
    borderColour: 'border-l-cyan-500',
    subChecks: [
      { label: 'C1 · Every file ≤ 500 lines' },
      { label: 'C2 · Domain-Driven Design (module by domain, not by layer)' },
      { label: 'C3 · Every Agent as a Product (versioned contract + owner + telemetry)' },
      { label: 'C4 · QA in Every Layer (unit + integration + e2e tests co-located with code)' },
      { label: 'C5 · Full Observability (structured logs + timing + user id on every path)' },
      { label: 'C6 · Version Everything (prompts, models, keys, flags, mockup versions)' },
    ],
  },
  {
    key: 'qabp',
    parent: 'QA Best Practice (Q1–Q5)',
    tooltip: 'Five QA Best Practice checks (Q1 Multi-Layer QA · Q2 Automated Regression · Q3 End-to-End Pipeline · Q4 Continuous Security · Q5 Quality Metrics + Release Gates).',
    borderColour: 'border-l-violet-500',
    subChecks: [
      { label: 'Q1 · Multi-Layer QA (unit + integration + contract + e2e)' },
      { label: 'Q2 · Automated Regression Testing (every bug fix carries a regression pytest)' },
      { label: 'Q3 · End-to-End Pipeline Testing (full flow user → DB → back)' },
      { label: 'Q4 · Continuous Security Testing (OWASP + authed-boundary tests every ship)' },
      { label: 'Q5 · Quality Metrics + Release Gates (uptime + error + latency + pass-rate alarms)' },
    ],
  },
  {
    key: 'mgd',
    parent: 'MGD · Mandatory Governance Document',
    tooltip: 'Mandatory Governance Document. Six sub-tasks. Both E1 and the Judge must confirm the MGD is loaded and quoted before any code write.',
    borderColour: 'border-l-fuchsia-500',
    subChecks: [
      { label: 'MGD.1 · E1 · Session Open · MGD read + recall-tested' },
      { label: 'MGD.2 · E1 · Intra-session · rule quoted before any code write' },
      { label: 'MGD.3 · E1 · Session Close · MGD delta captured in session-end summary' },
      { label: 'MGD.4 · Judge · Session Open · MGD read + recall-tested' },
      { label: 'MGD.5 · Judge · Intra-session · rule quoted before any PASS verdict' },
      { label: 'MGD.6 · Judge · Session Close · MGD delta captured in Judge memory' },
    ],
  },
  {
    key: 'fork',
    parent: 'T-FORK · Fork Handshake Integrity',
    tooltip: 'T-FORK. Six sub-tasks that fire only on fork sessions. Ensures the incoming E1 quotes back every pending operator directive verbatim before any code touches disk.',
    borderColour: 'border-l-orange-500',
    subChecks: [
      { label: 'FORK.1 · Retiring E1 wrote a "Pending operator directives · verbatim" block into the handoff' },
      { label: 'FORK.2 · Incoming E1 read the block first-thing on session open' },
      { label: 'FORK.3 · Incoming E1 quoted the block back to operator BEFORE any tool call' },
      { label: 'FORK.4 · Each pending directive got a Confirm / Revise / Drop verdict from operator' },
      { label: 'FORK.5 · Verdicts recorded in the ledger before the first code write' },
      { label: 'FORK.6 · § U.b post-fork verification pytest passes' },
    ],
  },
];

export const E1_WORK_LANDED_LABEL = 'How my work landed';
export const E1_WORK_LANDED = [
  { label: 'Done right, first time', tooltip: 'Task cleared all 11 § V.6 checks first-pass, no operator catches.' },
  { label: 'Wrong (operator caught before or after ship)', tooltip: 'Ground-truth flipped by operator to Wrong.' },
  { label: 'Corrected before ship (Judge caught me)', tooltip: 'Judge issued REVISE and I re-drafted before code hit disk.' },
  { label: 'Paused to ask you for clarification' },
  { label: 'Skipped (asked, not done)', tooltip: 'Operator asked, I did not do it, and did not surface the skip.' },
  { label: 'Actioned without your green light', tooltip: 'Executed a mutating action with no [OVERRIDE-REPLY] token.' },
  { label: 'Said I did but really didn\u2019t (attestation-honesty)', tooltip: 'Claimed a check ran when it did not. Anchor: GB-013.' },
  { label: 'QA / eyeball not executed', tooltip: 'Shipped without the promised bug testing agent invocation.' },
  { label: 'Section total (wrong + skipped + no-approval + attestation + QA)', bold: true },
];

export const E1_CONFIDENCE_LABEL = 'Confidence and learning';
export const E1_CONFIDENCE = [
  { label: 'E1 referred to past lessons', tooltip: 'Counter of turns where E1 quoted a LESSON: line from E1_Lessons.md before acting.' },
  { label: 'When E1 said HIGH confidence, was right', tooltip: 'HIGH-confidence self-assessments that survived operator ground-truth.' },
  { label: 'New lessons E1 codified today', tooltip: 'Fresh LESSON: entries appended to E1_Lessons.md this session.' },
  { label: 'Average E1 turn time', tooltip: 'Median wall-clock between operator message and first substantive reply.' },
];

export const E1_WORDS_LABEL = 'Words shown to users';
export const E1_WORDS = [
  { label: 'New copy strings I introduced' },
  { label: 'You approved' },
  { label: 'You revoked or backlogged', tooltip: 'Operator sent [REVOKE-COPY] or told me to backlog the string.' },
  { label: 'Waiting for your approval' },
  { label: 'Section total (new copy strings this session)', bold: true },
];

export const E1_LOOSE_ENDS_LABEL = 'Loose ends';
export const E1_LOOSE_ENDS = [
  { label: 'Rule-breaks still open' },
  { label: 'Rule-breaks that fired this session', tooltip: 'Number of GOVERNANCE_LEDGER breaches logged this session, resolved or otherwise.' },
  { label: 'Files over 500-line ceiling' },
];
